import { useMutation, useQueryClient } from '@tanstack/react-query';
import LeadService from '@clickersphere/core/services/LeadService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { LeadQueryData, LEADS_QUERY_KEY } from './useLeads';

export function useUpdateLeadStatus() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { mutateAsync: updateLeadStatusAsync } = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: boolean }) =>
      LeadService.updateStatus(id, value),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.id]),
      });

      const oldLeads = queryClient.getQueryData<LeadQueryData[]>(
        buildQueryKey(LEADS_QUERY_KEY),
      );
      const oldLead = queryClient.getQueryData<LeadQueryData>(
        buildQueryKey([LEADS_QUERY_KEY, variables.id]),
      );

      queryClient.setQueryData<LeadQueryData[]>(
        buildQueryKey(LEADS_QUERY_KEY),
        (old) =>
          old?.map((lead) =>
            lead.id === variables.id
              ? { ...lead, active: variables.value, queryStatus: 'pending' }
              : lead,
          ),
      );

      queryClient.setQueryData<LeadQueryData>(
        buildQueryKey([LEADS_QUERY_KEY, variables.id]),
        (old) =>
          old?.id === variables.id
            ? { ...old, queryStatus: 'pending', active: variables.value }
            : old,
      );

      return { oldLeads, oldLead };
    },
    onSuccess: async (_data, variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.id]),
      });
      queryClient.setQueryData<LeadQueryData[]>(
        buildQueryKey(LEADS_QUERY_KEY),
        (old) =>
          old?.map((lead) =>
            lead.id === variables.id
              ? { ...lead, queryStatus: undefined }
              : lead,
          ),
      );

      queryClient.setQueryData<LeadQueryData>(
        buildQueryKey([LEADS_QUERY_KEY, variables.id]),
        (old) =>
          old?.id === variables.id ? { ...old, queryStatus: undefined } : old,
      );
    },
    onError: async (_error, variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.id]),
      });

      queryClient.setQueryData<LeadQueryData[]>(
        buildQueryKey(LEADS_QUERY_KEY),
        context?.oldLeads,
      );

      queryClient.setQueryData<LeadQueryData>(
        buildQueryKey([LEADS_QUERY_KEY, variables.id]),
        context?.oldLead,
      );
    },
  });

  return {
    updateLeadStatusAsync,
  };
}
